<script setup lang="ts">
import { ref } from 'vue'
import {
  Type,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Terminal,
  List,
  ListOrdered,
  CheckSquare,
  Indent,
  Outdent,
  Link,
  Image as ImageIcon,
  Table,
  Quote,
  Minus,
  Superscript,
  SquareRadical,
  Calculator,
  FunctionSquare,
  RotateCcw,
  RotateCw,
} from 'lucide-vue-next'
import type { EditorAdapter } from '../core/editor-adapter'
import type { ToolbarGroup, ToolbarButton } from '../core/toolbar'
import { defaultToolbarGroups } from '../core/toolbar'

defineProps<{
  groups?: ToolbarGroup[]
  editor: EditorAdapter | null
  keymap?: any
}>()

const emit = defineEmits<{
  (e: 'action', action: string): void
}>()

const iconMap: Record<string, any> = {
  Type, Bold, Italic, Strikethrough, Code, Terminal, List, ListOrdered,
  CheckSquare, Indent, Outdent, Link, Image: ImageIcon, Table, Quote,
  Minus, Superscript, SquareRadical, Calculator, FunctionSquare,
  RotateCcw, RotateCw,
}

const groups = ref<ToolbarGroup[]>(defaultToolbarGroups)

function handleAction(btn: ToolbarButton) {
  emit('action', btn.name)
}
</script>

<template>
  <div class="markdown-editor__toolbar">
    <template v-for="group in groups" :key="group.name">
      <div
        class="markdown-editor__toolbar-group"
        v-if="group.buttons.length"
      >
        <button
          v-for="btn in group.buttons"
          :key="btn.name"
          class="markdown-editor__toolbar-btn"
          :title="btn.tooltip"
          @click="handleAction(btn)"
          tabindex="-1"
        >
          <component :is="iconMap[btn.icon] || Type" :size="16" />
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.markdown-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border);
  user-select: none;
}

.markdown-editor__toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-right: 8px;
  margin-right: 8px;
  border-right: 1px solid var(--color-border);
}

.markdown-editor__toolbar-group:last-child {
  border-right: none;
  margin-right: 0;
  padding-right: 0;
}

.markdown-editor__toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
}

.markdown-editor__toolbar-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text);
}

.markdown-editor__toolbar-btn:active {
  background: var(--color-border);
}
</style>